import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Search, Image as ImageIcon, MapPin, ShieldCheck, ShieldAlert, Phone, KeyRound, Flag, ExternalLink, MessageSquareText } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError, BACKEND_BASE } from "@/lib/api";

const PILLS = [
  { id: "pending", label: "Pending" },
  { id: "needs_review", label: "Needs Review" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];
const STATUS_TONE = {
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
  pending: "bg-amber-100 text-amber-700",
  needs_review: "bg-orange-100 text-orange-700",
};
const STATUS_LABEL = { needs_review: "Needs review", approved: "Approved", rejected: "Rejected", pending: "Pending" };

function NoteField({ value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label>Notes / reason <span className="text-slate-400 font-normal">(optional)</span></Label>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} maxLength={500} rows={3}
        className="mt-1.5 resize-none" placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

const DECISION = {
  reject: { title: "Reject", path: "reject", done: "Buyer rejected", btn: "Reject application", tone: "bg-rose-600 hover:bg-rose-700",
    hint: "The buyer will not be able to see prices or order.", ph: "e.g. Rejected – visiting card appears AI-generated" },
  escalate: { title: "Escalate", path: "escalate", done: "Moved to Needs Review", btn: "Move to Needs Review", tone: "bg-orange-500 hover:bg-orange-600",
    hint: "The application is neither approved nor rejected; it waits in the Needs Review tab.", ph: "e.g. GSTIN name doesn't match shop – call before approving" },
};

function DecisionDialog({ buyer, kind, onClose, onDone }) {
  const cfg = DECISION[kind];
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/buyers/${buyer.id}/${cfg.path}`, { note });
      toast.success(cfg.done);
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`${kind}-modal`}>
        <DialogHeader>
          <DialogTitle>{cfg.title} {buyer.company_name}</DialogTitle>
          <DialogDescription>{cfg.hint}</DialogDescription>
        </DialogHeader>
        <NoteField value={note} onChange={setNote} placeholder={cfg.ph} testid={`${kind}-note-input`} />
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className={`w-full rounded-full text-white ${cfg.tone}`} data-testid={`${kind}-confirm-btn`}>
            {saving ? "Saving…" : cfg.btn}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewNotes({ notes, tid }) {
  if (!notes?.length) return null;
  return (
    <div className="mt-2 space-y-1.5 border-l-2 border-slate-200 pl-3" data-testid={`review-notes-${tid}`}>
      {notes.slice().reverse().map((n, i) => (
        <div key={i} className="text-xs text-slate-600">
          <span className={`inline-block rounded px-1.5 py-0.5 mr-1.5 font-semibold ${STATUS_TONE[n.status] || "bg-slate-100"}`}>{STATUS_LABEL[n.status] || n.status}</span>
          {n.note ? <span className="text-slate-800">{n.note}</span> : <span className="italic text-slate-400">No note</span>}
          <span className="text-slate-400"> · {n.by} · {new Date(n.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
        </div>
      ))}
    </div>
  );
}

function TermsDialog({ buyer, mode, onClose, onDone }) {
  const [payLater, setPayLater] = useState(!!buyer?.allow_pay_later);
  const [terms, setTerms] = useState(buyer?.payment_terms || 0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  if (!buyer) return null;

  const submit = async () => {
    setSaving(true);
    const body = { allow_pay_later: payLater, payment_terms: Number(terms) || 0 };
    try {
      if (mode === "approve") {
        const { data } = await api.post(`/admin/buyers/${buyer.id}/approve`, { ...body, note });
        toast.success(data.zoho_contact_id ? `Approved · linked to Zoho contact ${data.zoho_contact_id}` : "Buyer approved (Zoho contact not created — check Zoho connection)");
      } else {
        await api.patch(`/admin/buyers/${buyer.id}`, body);
        toast.success("Payment terms updated");
      }
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="approve-modal">
        <DialogHeader>
          <DialogTitle>{mode === "approve" ? "Approve" : "Edit terms for"} {buyer.company_name}</DialogTitle>
          <DialogDescription>
            {mode === "approve" ? "Approval unlocks prices & ordering and creates / links the buyer's Zoho customer." : "Change Pay Later access and credit days."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
            <div>
              <div className="font-medium text-sm text-slate-900">Allow Pay Later</div>
              <div className="text-xs text-slate-500">Shows "Pay Later" at checkout for this buyer only</div>
            </div>
            <Switch checked={payLater} onCheckedChange={setPayLater} data-testid="approve-paylater-toggle" />
          </div>
          <div>
            <Label>Payment terms (days)</Label>
            <Input type="number" min="0" value={terms} onChange={(e) => setTerms(e.target.value)} className="mt-1.5" placeholder="e.g. 30" data-testid="approve-terms-input" />
            <p className="text-xs text-slate-400 mt-1">0 = due on receipt. Saved to the Zoho customer as "Net N".</p>
          </div>
          {mode === "approve" && (
            <NoteField value={note} onChange={setNote} placeholder="e.g. Auto-approved – existing customer in Zoho Inventory" testid="approve-note-input" />
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-confirm-btn">
            {saving ? "Saving…" : mode === "approve" ? "Approve buyer" : "Save terms"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPwDialog({ buyer, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/buyers/${buyer.id}/reset-password`, { password: pw });
      toast.success(`Password updated for ${buyer.company_name}. Share it with them directly.`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="admin-reset-modal">
        <DialogHeader>
          <DialogTitle>Reset password for {buyer.company_name}</DialogTitle>
          <DialogDescription>Set a new password and share it with the buyer by phone or WhatsApp. They can change it later via "Forgot password".</DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="admin-new-pw">New password</Label>
          <Input id="admin-new-pw" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" placeholder="At least 6 characters" data-testid="admin-reset-input" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || pw.trim().length < 6} className="w-full rounded-full bg-slate-900 hover:bg-black text-white" data-testid="admin-reset-confirm">
            {saving ? "Saving…" : "Set new password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function shortLink(url) {
  try {
    const u = new URL(url);
    return (u.host + u.pathname).replace(/^www\./, "").slice(0, 38);
  } catch {
    return url.slice(0, 38);
  }
}

function BuyerCard({ b, onApprove, onEdit, onReject, onEscalate, onRefresh, onResetPw }) {
  const tid = b.id;
  const where = [b.city, b.state].filter(Boolean).join(", ");
  return (
    <div data-testid={`buyer-row-${tid}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-slate-900">{b.company_name}</span>
          <Badge className={`border-0 ${STATUS_TONE[b.status] || STATUS_TONE.pending}`} data-testid={`buyer-status-${tid}`}>{STATUS_LABEL[b.status] || b.status}</Badge>
          {b.gst_verified ? (
            <Badge className="border-0 bg-emerald-50 text-emerald-700" data-testid={`gst-badge-${tid}`}><ShieldCheck className="h-3 w-3 mr-1" />GST Verified</Badge>
          ) : (
            <Badge className="border-0 bg-slate-100 text-slate-600" data-testid={`gst-badge-${tid}`}><ShieldAlert className="h-3 w-3 mr-1" />{b.gstin ? "GSTIN invalid" : "Unregistered"}</Badge>
          )}
          {b.allow_pay_later && <Badge className="border-0 bg-indigo-50 text-indigo-700" data-testid={`paylater-badge-${tid}`}>Pay Later · Net {b.payment_terms || 0}</Badge>}
          {b.zoho_contact_id && <Badge className="border-0 bg-sky-100 text-sky-700" title={b.zoho_contact_id}>Zoho linked</Badge>}
          {!b.zoho_contact_id && b.zoho_match && (
            <Badge className="border-0 bg-violet-100 text-violet-700" title={`Zoho contact ${b.zoho_match.contact_id}`} data-testid={`zoho-match-${tid}`}>
              <Search className="h-3 w-3 mr-1" />Found in Zoho · by {b.zoho_match.by}{b.zoho_match.name ? ` · ${b.zoho_match.name}` : ""}
            </Badge>
          )}
        </div>
        <div className="text-sm text-slate-500">{b.contact_person}{b.business_type ? ` · ${b.business_type}` : ""}</div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          {b.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{b.phone}</span>}
          <span className="inline-flex items-center gap-1" data-testid={`buyer-city-${tid}`}><MapPin className="h-3 w-3" />{where || "City not given"}</span>
          {b.gstin && <span className="font-mono-num">GSTIN {b.gstin}</span>}
        </div>
        <div className="flex flex-wrap gap-3 pt-1">
          {b.has_shop_image && (
            <a href={`${BACKEND_BASE}${b.shop_image}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:underline" data-testid={`shop-doc-${tid}`}>
              <ImageIcon className="h-3.5 w-3.5" /> Shop photo
            </a>
          )}
          {b.google_location && (
            <a href={b.google_location} target="_blank" rel="noopener noreferrer" title={b.google_location}
              className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700 hover:bg-sky-100 max-w-full" data-testid={`shop-location-${tid}`}>
              <MapPin className="h-3.5 w-3.5 shrink-0" /> Google Maps / Business
              <span className="hidden sm:inline text-sky-500 font-normal truncate">· {shortLink(b.google_location)}</span>
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          )}
        </div>
        {b.review_notes?.length > 0 && (
          <div className="pt-1 text-xs font-semibold text-slate-500 inline-flex items-center gap-1"><MessageSquareText className="h-3.5 w-3.5" /> Review notes</div>
        )}
        <ReviewNotes notes={b.review_notes} tid={tid} />
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0 md:justify-end md:max-w-md">
        {b.status === "approved" && !b.zoho_contact_id && (
          <Button size="sm" variant="outline" className="rounded-full text-rose-600 border-rose-200" data-testid={`retry-zoho-contact-${tid}`}
            onClick={async () => { try { const { data } = await api.post(`/admin/buyers/${b.id}/push-zoho`); toast.success(`Linked to Zoho contact ${data.zoho_contact_id}`); onRefresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>
            Push to Zoho
          </Button>
        )}
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onResetPw(b)} data-testid={`reset-pw-${tid}`}>
          <KeyRound className="h-4 w-4 mr-1" /> Reset password
        </Button>
        {b.status === "approved" && (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEdit(b)} data-testid={`edit-terms-${tid}`}>Edit terms</Button>
        )}
        {b.status !== "approved" && (
          <Button size="sm" onClick={() => onApprove(b)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-buyer-${tid}`}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
          </Button>
        )}
        {b.status === "pending" && (
          <Button size="sm" variant="outline" onClick={() => onEscalate(b)} className="rounded-full text-orange-600 border-orange-200 hover:bg-orange-50" data-testid={`escalate-buyer-${tid}`}>
            <Flag className="h-4 w-4 mr-1" /> Needs Review
          </Button>
        )}
        {b.status !== "rejected" && (
          <Button size="sm" variant="outline" onClick={() => onReject(b)} className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`reject-buyer-${tid}`}>
            <XCircle className="h-4 w-4 mr-1" /> Reject
          </Button>
        )}
      </div>
    </div>
  );
}

export default function BuyerApprovals({ onChange }) {
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ buyers: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [resetBuyer, setResetBuyer] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/buyers", { params: { status, ...(q.trim() ? { q: q.trim() } : {}) } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q]);

  const refresh = () => { load(); onChange?.(); };
  const [decision, setDecision] = useState(null);

  return (
    <div className="space-y-4" data-testid="buyer-approvals">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex gap-2 overflow-x-auto" data-testid="buyer-status-pills">
          {PILLS.map((p) => (
            <button key={p.id} onClick={() => setStatus(p.id)} data-testid={`buyer-pill-${p.id}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${status === p.id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
              {p.label} <span className="ml-1 text-xs opacity-60">{data.counts?.[p.id] ?? ""}</span>
            </button>
          ))}
        </div>
        <div className="relative sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, city, GSTIN…" className="pl-9 rounded-full bg-white" data-testid="buyer-search-input" />
        </div>
      </div>
      {!loading && data.buyers.length === 0 && (
        <div className="text-center py-12 text-slate-400 bg-white rounded-2xl border" data-testid="buyers-empty">No buyers here.</div>
      )}
      {data.buyers.map((b) => (
        <BuyerCard key={b.id} b={b} onRefresh={refresh} onResetPw={setResetBuyer}
          onReject={(x) => setDecision({ buyer: x, kind: "reject" })}
          onEscalate={(x) => setDecision({ buyer: x, kind: "escalate" })}
          onApprove={(x) => setDialog({ buyer: x, mode: "approve" })}
          onEdit={(x) => setDialog({ buyer: x, mode: "edit" })} />
      ))}
      {dialog && <TermsDialog buyer={dialog.buyer} mode={dialog.mode} onClose={() => setDialog(null)} onDone={refresh} />}
      {decision && <DecisionDialog buyer={decision.buyer} kind={decision.kind} onClose={() => setDecision(null)} onDone={refresh} />}
      {resetBuyer && <ResetPwDialog buyer={resetBuyer} onClose={() => setResetBuyer(null)} />}
    </div>
  );
}
